import React, { useState, useEffect } from 'react'
import { Alert, Button, Col, Form, Row } from 'react-bootstrap'
import Select from 'react-select'
import sicCodeService from '@rd-web-markets/shared/dist/services/sic_codes/sic_code.service'
import sicCodeSectionService from '@rd-web-markets/shared/dist/services/sic_codes/sic_code_section.service'
import { DeleteButton } from '@rd-web-markets/shared/dist/util/buttons'
import './CompanyDetailsStep.css'

const DASHBOARD_TYPES = [
  { value: 'cost_category_breakdown', label: 'Cost Category Breakdown' },
  { value: 'business_unit_intensity_breakdown', label: 'Business Unit Intensity Breakdown' },
]

export default function CompanyDetailsStep({ company, onChange, setCompany, onNext }) {
  const [validationError, setValidationError] = useState(null)
  const [sicCodes, setSicCodes] = useState([])
  const [sicCodeSections, setSicCodeSections] = useState([])
  const [params, setParams] = useState()

  const [subsidiaries, setSubsidiaries] = useState(
    company.subsidiary_companies_attributes || []
  )
  const [newSubName, setNewSubName] = useState('')
  const [newSubUtr, setNewSubUtr] = useState('')

  useEffect(() => {
    const fetchSicData = async () => {
      try {
        const res = await sicCodeService.get(params)
        setSicCodes(res.sic_codes || [])
      } catch (e) {}
    }
    fetchSicData()
  }, [params])

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const res = await sicCodeSectionService.get()
        setSicCodeSections(res.sic_code_sections || [])
      } catch (e) {}
    }
    fetchSections()
  }, [])

  const sicCodeOptions = sicCodes.map(s => ({ value: s.id, label: `(${s.number}) ${s.title}`, sic_code_section_id: s.sic_code_section_id }))
  const sicCodeSectionOptions = sicCodeSections.map(s => ({ value: s.id, label: s.name }))
  const dashboardTypeOptions = DASHBOARD_TYPES

  const toggleRadio = (name, value) => {
    onChange({ target: { name, value } })
  }

  const setSelectedSicCodeSectionState = (e) => {
    setCompany({ ...company, sic_code_section: e.value })
    setParams(`by_section=${e.value}`)
  }

  const setSicCodeChange = (e) => {
    setCompany({ ...company, sic_code_id: e.value, sic_code_section: e.sic_code_section_id })
  }

  const setDashboardType = (e) => {
    setCompany({ ...company, dashboard_type: e.value })
  }

  const addSubsidiary = () => {
    if (!newSubName.trim()) return
    const updated = [...subsidiaries, { name: newSubName.trim(), utr: newSubUtr.trim() }]
    setSubsidiaries(updated)
    setCompany({ ...company, subsidiary_companies_attributes: updated })
    setNewSubName('')
    setNewSubUtr('')
  }

  const removeSubsidiary = (index) => {
    const updated = subsidiaries.filter((_, i) => i !== index)
    setSubsidiaries(updated)
    setCompany({ ...company, subsidiary_companies_attributes: updated })
  }

  const validateName = () => {
    if (!company.name?.trim()) {
      setValidationError('Company name is required')
      return false
    }
    setValidationError(null)
    return true
  }

  const goToNextStep = () => {
    if (validateName()) onNext()
  }

  const selectStyles = {
    control: (base) => ({ ...base, borderColor: '#80bbdc' })
  }

  return (
    <div className="company-details-step">
      <h5 className="company-details-step__title">Step 1 — Company Details</h5>

      {validationError && (
        <Alert variant="danger" className="company-details-step__alert">{validationError}</Alert>
      )}

      <FormRow label="Company Name" required>
        <Form.Control
          type="text"
          name="name"
          value={company.name || ''}
          onChange={onChange}
          className={!company.name?.trim() && validationError ? 'company-details-step__input--invalid' : ''}
        />
      </FormRow>

      <FormRow label="Company Abbreviated Name">
        <Form.Control
          type="text"
          name="abbreviated_name"
          value={company.abbreviated_name || ''}
          onChange={onChange}
        />
      </FormRow>

      <FormRow label="VAT Number">
        <Form.Control
          type="text"
          name="vat_number"
          value={company.vat_number || ''}
          onChange={onChange}
        />
      </FormRow>

      <FormRow label="Has PAYE Reference Number?">
        <div className="company-details-step__radio-group">
          <Form.Check
            inline type="radio" label="Yes" value="true"
            checked={company.has_paye_ref_number?.toString() === 'true'}
            onChange={() => toggleRadio('has_paye_ref_number', 'true')}
          />
          <Form.Check
            inline type="radio" label="No" value="false"
            checked={company.has_paye_ref_number?.toString() === 'false'}
            onChange={() => toggleRadio('has_paye_ref_number', 'false')}
          />
        </div>
      </FormRow>

      {company.has_paye_ref_number?.toString() === 'true' && (
        <FormRow label="PAYE Ref Number">
          <Form.Control
            type="text"
            name="paye_ref_number"
            value={company.paye_ref_number || ''}
            onChange={onChange}
          />
        </FormRow>
      )}

      <FormRow label="Claimed R&D tax credits before?">
        <div className="company-details-step__radio-group">
          <Form.Check
            inline type="radio" label="Yes" value="true"
            checked={company.has_made_claims_before?.toString() === 'true'}
            onChange={() => toggleRadio('has_made_claims_before', 'true')}
          />
          <Form.Check
            inline type="radio" label="No" value="false"
            checked={company.has_made_claims_before?.toString() === 'false'}
            onChange={() => toggleRadio('has_made_claims_before', 'false')}
          />
        </div>
      </FormRow>

      <FormRow label="Has the company accounting period changed?">
        <div className="company-details-step__radio-group">
          <Form.Check
            inline type="radio" label="Yes" value="true"
            checked={company.accounts_period_changed?.toString() === 'true'}
            onChange={() => toggleRadio('accounts_period_changed', 'true')}
          />
          <Form.Check
            inline type="radio" label="No" value="false"
            checked={company.accounts_period_changed?.toString() === 'false'}
            onChange={() => toggleRadio('accounts_period_changed', 'false')}
          />
        </div>
      </FormRow>

      <FormRow label="Business Unit Required">
        <div className="company-details-step__radio-group">
          <Form.Check
            inline type="radio" label="Yes" value="true"
            checked={company.business_unit_required?.toString() === 'true'}
            onChange={() => toggleRadio('business_unit_required', 'true')}
          />
          <Form.Check
            inline type="radio" label="No" value="false"
            checked={company.business_unit_required?.toString() === 'false'}
            onChange={() => toggleRadio('business_unit_required', 'false')}
          />
        </div>
      </FormRow>

      <FormRow label="Has Subsidiary Companies?">
        <div className="company-details-step__radio-group">
          <Form.Check
            inline type="radio" label="Yes" value="true"
            checked={company.has_subsidiary_companies?.toString() === 'true'}
            onChange={() => toggleRadio('has_subsidiary_companies', 'true')}
          />
          <Form.Check
            inline type="radio" label="No" value="false"
            checked={company.has_subsidiary_companies?.toString() === 'false'}
            onChange={() => toggleRadio('has_subsidiary_companies', 'false')}
          />
        </div>
      </FormRow>

      {company.has_subsidiary_companies?.toString() === 'true' && (
        <FormRow label="Requires separate AIF submission?">
          <div className="company-details-step__radio-group">
            <Form.Check
              inline type="radio" label="Yes" value="true"
              checked={company.requires_separate_aif_submission?.toString() === 'true'}
              onChange={() => toggleRadio('requires_separate_aif_submission', 'true')}
            />
            <Form.Check
              inline type="radio" label="No" value="false"
              checked={company.requires_separate_aif_submission?.toString() === 'false'}
              onChange={() => toggleRadio('requires_separate_aif_submission', 'false')}
            />
          </div>
        </FormRow>
      )}

      {company.has_subsidiary_companies?.toString() === 'true' && (
        <div className="company-details-step__subsidiary-section">
          <div className="company-details-step__subsidiary-title">Subsidiaries</div>
          <div className="company-details-step__subsidiary-note">
            Note: Additional subsidiary details (company number, VAT, SIC code etc.) can be added after the company is created.
          </div>

          {subsidiaries.length > 0 && (
            <div className="company-details-step__subsidiary-list">
              {subsidiaries.map((sub, i) => (
                <div key={i} className="company-details-step__subsidiary-row">
                  <div className="company-details-step__subsidiary-info">
                    <span className="company-details-step__subsidiary-name">{sub.name}</span>
                    {sub.utr && <span className="company-details-step__subsidiary-utr">UTR: {sub.utr}</span>}
                  </div>
                  <DeleteButton onClick={() => removeSubsidiary(i)} />
                </div>
              ))}
            </div>
          )}

          <Row className="g-2 align-items-end mt-2">
            <Col md={5}>
              <Form.Label className="company-details-step__sub-label">Subsidiary Name</Form.Label>
              <Form.Control
                type="text"
                value={newSubName}
                onChange={e => setNewSubName(e.target.value)}
                placeholder="Enter name..."
                size="sm"
              />
            </Col>
            <Col md={4}>
              <Form.Label className="company-details-step__sub-label">UTR</Form.Label>
              <Form.Control
                type="text"
                value={newSubUtr}
                onChange={e => setNewSubUtr(e.target.value)}
                placeholder="Enter UTR..."
                size="sm"
              />
            </Col>
            <Col md={3}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={addSubsidiary}
                disabled={!newSubName.trim()}
                className="company-details-step__add-btn"
              >
                + Add
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {company.has_subsidiary_companies?.toString() === 'false' && (
        <FormRow label="Company Unique Tax Reference (UTR)">
          <Form.Control
            type="text"
            name="utr"
            value={company.utr || ''}
            onChange={onChange}
          />
        </FormRow>
      )}

      <FormRow label="SIC Code Section">
        <Select
          value={sicCodeSectionOptions.find(o => o.value === company.sic_code_section) || null}
          onChange={setSelectedSicCodeSectionState}
          options={sicCodeSectionOptions}
          styles={selectStyles}
          placeholder="Select section..."
        />
      </FormRow>

      <FormRow label="Industry / Sector (SIC)">
        <Select
          value={sicCodeOptions.find(o => o.value === company.sic_code_id) || null}
          onChange={setSicCodeChange}
          options={sicCodeOptions}
          styles={selectStyles}
          placeholder="Select SIC code..."
        />
      </FormRow>

      <FormRow label="Dashboard Type">
        <Select
          value={dashboardTypeOptions.find(o => o.value === company.dashboard_type) || null}
          onChange={setDashboardType}
          options={dashboardTypeOptions}
          styles={selectStyles}
          placeholder="Select dashboard type..."
        />
      </FormRow>

      <FormRow label="Company Logo">
        <Form.Control
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0]
            if (!file) return
            const reader = new FileReader()
            reader.onloadend = () => {
              const base64 = reader.result.split(',')[1]
              setCompany({ ...company, image_data: base64 })
            }
            reader.readAsDataURL(file)
          }}
        />
        {company.image_data && (
          <img
            src={`data:image/png;base64,${company.image_data}`}
            alt="Company logo preview"
            className="company-details-step__logo-preview"
          />
        )}
      </FormRow>

      <div className="company-details-step__footer">
        <Button variant="primary" onClick={goToNextStep} className="company-details-step__next-btn">
          Continue →
        </Button>
      </div>
    </div>
  )
}

function FormRow({ label, children, required }) {
  return (
    <Form.Group as={Row} className="mb-3">
      <Form.Label column md={5} className="company-details-step__label">
        {label}{required && <span className="company-details-step__required"> *</span>}
      </Form.Label>
      <Col md={7}>
        {children}
      </Col>
    </Form.Group>
  )
}
